import {
    Autocomplete,
    Box,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    InputAdornment,
    Stack,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography,
    createFilterOptions,
} from '@mui/material';
import {useCallback, useEffect, useMemo, useState} from 'react';

import {AuthSession} from '../App';
import {ChipKind, nameFromEmail} from '../lib/aggregate';
import {
    AI_ACCOUNTS,
    AI_ACCOUNT_GROUP_LABEL,
    TENANT_GROUP_LABEL,
    aiAccountLabel,
    isAiAccount,
    isValidResourceForKind,
    resourceAxisLabel,
} from '../lib/ai-accounts';
import {CreateRequestInput, Me, TenantCatalogEntry, UserProfile, createRequest} from '../lib/api';
import {GrantView} from '../lib/grant-view';
import {
    classifyImpAiSubject,
    formatImpAiSubject,
    impAiSubjectError,
    normalizeImpAiSubject,
} from '../lib/imp-ai-subject';
import {RequestPrefill} from '../lib/request-deep-link';

import {UserBadge} from './UserBadge';

interface Props {
    open: boolean;
    onClose: () => void;
    session: AuthSession;
    me: Me;
    /** Used to derive the list of tenants for the picker — same source as GrantsPage. */
    grants: GrantView[];
    /**
     * Full tenant catalog (every tenant, even ones with no grant yet). Seeds the
     * picker; `grants` then enriches it. Empty when the catalog fetch failed, in
     * which case the picker falls back to grants-derived tenants only.
     */
    tenantCatalog: TenantCatalogEntry[];
    /** Admin-only: cached user list used to populate the recipient combobox. */
    users?: UserProfile[];
    /**
     * Pre-filled access type and subject from a deep link (see
     * `lib/request-deep-link`). Applied when the dialog opens; the operator can
     * still change everything, and the recipient/reason are always theirs to fill.
     */
    prefill?: RequestPrefill;
}

const MIN_REASON_LENGTH = 20;
const REASON_PLACEHOLDER =
    "What are you doing and why? E.g. 'Debugging policy rollup mismatch in Acme's commercial book per ticket ENG-4421.'";
const WILDCARD = '*';
const RESET_DELAY_MS = 200;
const AWS_ACCOUNT_ID = /^\d{12}$/;

// Every access type is a real capability now. There is no placeholder card, so
// KindId is exactly the chip kinds — keep it that way: a disabled "coming soon"
// card is unreachable UI that drifts untested.
type KindId = ChipKind;
type LockedExpiry = 'permanent';
type ExpiryChoice = '1d' | '7d' | 'custom' | 'permanent';

interface KindMeta {
    id: KindId;
    label: string;
    desc: string;
    /** Raw HSL — the only intentionally hardcoded colour per kind; tokens elsewhere. */
    dotColor: string;
    /** Locks Expires to this value and disables the other choices with a tooltip. */
    lockedExpiry?: LockedExpiry;
    lockedNote?: string;
}

// Ennabl AI first, Decisions last — the same order the tabs use.
//
// Only Decisions locks Expires to Permanent. Ennabl AI deliberately allows the
// presets: its grant is a capability rather than a per-target list, and the
// time-box is what keeps that from being a standing key.
const KIND_LIST: readonly KindMeta[] = [
    {
        id: 'imp-ai',
        label: 'Ennabl AI Impersonate',
        desc: 'Log in to Ennabl AI as a user in the tenant.',
        dotColor: 'hsl(165 62% 45%)',
    },
    {
        id: 'data-access',
        label: 'AWS Data Access',
        desc: 'STS-AssumeRole into an AWS account (read-only). Tenants and Ennabl AI accounts.',
        dotColor: 'hsl(206 100% 50%)',
    },
    {
        id: 'imp-decisions',
        label: 'Decisions Impersonate',
        desc: 'Log in to Decisions as a user in the tenant.',
        dotColor: 'hsl(285 55% 50%)',
        lockedExpiry: 'permanent',
        lockedNote: 'Decisions impersonation is restricted to Permanent grants.',
    },
] as const;

const KIND_BY_ID: Record<KindId, KindMeta> = Object.fromEntries(KIND_LIST.map(k => [k.id, k])) as Record<
    KindId,
    KindMeta
>;

interface FormState {
    recipient: UserProfile | null;
    kind: KindId;
    /** Canonical tenant names; `['*']` is the wildcard sentinel. */
    tenants: string[];
    expiry: ExpiryChoice;
    customDays: number;
    reason: string;
}

interface TenantOption {
    /** Canonical key — the tenant name when known, otherwise the raw shardId. */
    value: string;
    /** Long label for the dropdown (name + AWS ids). */
    label: string;
    ids: string[];
}

function initialState(prefill?: RequestPrefill): FormState {
    const kind = prefill?.kind ?? 'data-access';
    return {
        recipient: null,
        kind,
        tenants: prefill?.subject ? [prefill.subject] : [],
        // Respect the type's own lock rather than the default: arriving on
        // Decisions with `7d` would show a value the control cannot hold.
        expiry: KIND_BY_ID[kind]?.lockedExpiry ?? '7d',
        customDays: 14,
        // Deliberately NOT pre-filled. The reason is the justification an approver
        // reads; a deep link cannot know it, and seeding it with something generic
        // would turn a required field into a rubber stamp.
        reason: '',
    };
}

/**
 * Modal dialog for creating an access grant (admin) or request (user). One grant
 * kind per submission. The internal form shape is independent of the backend
 * payload — `buildSubmitInput` translates to the existing CreateRequestInput so
 * functions/ stays untouched.
 */
export function NewRequestDialog({
    open,
    onClose,
    session,
    me,
    grants,
    tenantCatalog,
    users,
    prefill,
}: Props) {
    const adminMode = me.role === 'admin';
    const [form, setForm] = useState<FormState>(() => initialState(prefill));
    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState<string | undefined>();
    const [showErrors, setShowErrors] = useState(false);
    /**
     * What is typed into the resource field but not yet turned into a token.
     *
     * Both axes need it, for opposite reasons. On the SUBJECT axis (freeSolo,
     * commits on Enter and nothing else) the draft is folded into the form and
     * treated as a token, because an operator who typed a subject and clicked
     * Grant access meant that subject. On a PICKER axis free text can never
     * become a token, so the draft is instead what lets the dialog SAY that,
     * naming the text rather than dropping it. Either way the rule is the same:
     * a field that silently swallows what you typed is indistinguishable from a
     * broken one.
     */
    const [resourceDraft, setResourceDraft] = useState('');
    /**
     * The same idea for the Recipient picker: text that matched no colleague.
     * The roster only holds people who have signed in to AccessOps at least
     * once, so a perfectly valid address can match nothing — and until this was
     * named, the box simply went empty on blur while the form complained about
     * a field the admin had visibly filled in.
     */
    const [recipientDraft, setRecipientDraft] = useState('');

    useEffect(() => {
        if (open) {
            setForm(initialState(prefill));
            setResourceDraft('');
            setRecipientDraft('');
            setSubmitting(false);
            setSubmitError(undefined);
            setShowErrors(false);
        }
    }, [open, prefill]);

    const tenantOptions = useMemo<TenantOption[]>(
        () => buildTenantOptions(grants, tenantCatalog),
        [grants, tenantCatalog]
    );
    /**
     * The AI accounts, as their own option group. Offered on Data Access ONLY:
     * they are AWS accounts, so no impersonation kind may target them.
     */
    const aiAccountOptions = useMemo<TenantOption[]>(
        () => AI_ACCOUNTS.map(a => ({value: a.id, label: `${a.name} · ${a.id}`, ids: [a.id]})),
        []
    );
    const offersAiAccounts = form.kind === 'data-access';
    /**
     * Ennabl AI has no catalog to pick from — a subject is an email, a domain, or
     * `*`, typed by hand. So the field becomes free-text with only the wildcard
     * offered as a suggestion, and validation carries the weight the option list
     * used to.
     */
    const isSubjectAxis = form.kind === 'imp-ai';
    /**
     * Ennabl AI accounts first, then the tenant group led by the wildcard.
     *
     * The wildcard belongs INSIDE the tenant group (it means all tenants, and it
     * never covers an AI account). Emitting it before the AI group would make MUI
     * open a "Tenant accounts" heading, then the AI heading, then a SECOND
     * "Tenant accounts" heading — `groupBy` starts a new group on every change of
     * consecutive value. Two concatenated lists, never sorted together, so the
     * groups cannot interleave.
     */
    const tenantValues = useMemo(
        () =>
            isSubjectAxis
                ? [WILDCARD]
                : [
                      ...(offersAiAccounts ? aiAccountOptions.map(o => o.value) : []),
                      WILDCARD,
                      ...tenantOptions.map(o => o.value),
                  ],
        [isSubjectAxis, offersAiAccounts, aiAccountOptions, tenantOptions]
    );
    const tenantLabelOf = useCallback(
        (value: string) => {
            if (value === WILDCARD) {
                // On the subject axis `*` means every Ennabl AI USER; elsewhere it
                // means all TENANTS (and never an AI account, which must be
                // granted explicitly).
                return isSubjectAxis ? 'Everyone (*)' : 'All tenants (*)';
            }
            if (isSubjectAxis) {
                return formatImpAiSubject(value);
            }
            return (
                aiAccountOptions.find(o => o.value === value)?.label ??
                tenantOptions.find(o => o.value === value)?.label ??
                value
            );
        },
        [isSubjectAxis, aiAccountOptions, tenantOptions]
    );
    /** Group heading a value belongs under, or undefined when groups are off. */
    const tenantGroupOf = useCallback(
        (value: string) => {
            if (!offersAiAccounts) {
                return undefined;
            }
            // The wildcard sits at the head of the tenant group, not in its own.
            return isAiAccount(value) ? AI_ACCOUNT_GROUP_LABEL : TENANT_GROUP_LABEL;
        },
        [offersAiAccounts]
    );

    const activeKind = KIND_BY_ID[form.kind];
    /**
     * MUI's own default filter, so "does the typed text match anything?" is
     * answered by the exact rule the dropdown uses. A hand-rolled substring test
     * would drift from the list the operator is looking at.
     */
    const matchOptions = useMemo(() => createFilterOptions<string>(), []);
    /**
     * Draft text in a PICKER that matches no option. It can never become a token,
     * so submitting would drop it silently — name it instead.
     */
    const unmatchedDraft = useMemo(() => {
        const raw = resourceDraft.trim();
        if (isSubjectAxis || !raw) {
            return '';
        }
        const matches = matchOptions(tenantValues, {inputValue: raw, getOptionLabel: tenantLabelOf});
        return matches.length === 0 ? raw : '';
    }, [isSubjectAxis, resourceDraft, matchOptions, tenantValues, tenantLabelOf]);
    /**
     * Recipient text that matches nobody in the roster. Same rule as
     * `unmatchedDraft` on the resource axis, and the same predicate the picker
     * filters with, so the verdict cannot drift from the list being looked at.
     */
    const unmatchedRecipient = useMemo(() => {
        const raw = recipientDraft.trim();
        if (!adminMode || form.recipient || !raw) {
            return '';
        }
        return matchesRecipient(users ?? [], raw) ? '' : raw;
    }, [adminMode, form.recipient, recipientDraft, users]);
    /** `form` with the uncommitted subject text folded in — see `resourceDraft`. */
    const effectiveForm = useMemo(
        () => withSubjectDraft(form, isSubjectAxis ? resourceDraft : ''),
        [form, isSubjectAxis, resourceDraft]
    );
    const validation = useMemo(
        () => firstIssue(effectiveForm, adminMode, unmatchedDraft, unmatchedRecipient),
        [effectiveForm, adminMode, unmatchedDraft, unmatchedRecipient]
    );
    const isValid = validation === null;
    /** The resource-axis half of the validation, rendered under its own field. */
    const resourceProblem = resourceIssue(effectiveForm, unmatchedDraft);
    /** The recipient half, rendered under its own field for the same reason. */
    const recipientProblem = recipientIssue(form, adminMode, unmatchedRecipient);

    const selectKind = (next: KindId) => {
        const meta = KIND_BY_ID[next];
        // The draft is deliberately NOT cleared: switching the access type does
        // not empty the resource box, so dropping the draft would leave the form
        // complaining about a field the operator can see they filled in. Each
        // axis judges the same text on its own terms — folded in as a subject,
        // or named as something no picker option matches.
        setForm(f => ({
            ...f,
            kind: next,
            // Leaving Data Access drops AI accounts: they are AWS accounts, not
            // tenants, so they cannot be an impersonation resource (the server
            // rejects the pair too — this just avoids a guaranteed failure).
            tenants: f.tenants.filter(t => isValidResourceForKind(next, t)),
            // Coming off a ∞-locked type lands on the default rather than the
            // narrowest option — 7 days is the intended shape for Ennabl AI.
            expiry: meta.lockedExpiry ?? (f.expiry === 'permanent' && !meta.lockedExpiry ? '7d' : f.expiry),
        }));
    };

    const handleClose = () => {
        onClose();
        window.setTimeout(() => {
            // Reset to a CLEAN form, not the prefill: the deep link was consumed
            // when it opened, and re-applying it on close would resurrect a
            // subject the operator may have just decided against.
            setForm(initialState());
            setResourceDraft('');
            setRecipientDraft('');
            setSubmitError(undefined);
            setShowErrors(false);
        }, RESET_DELAY_MS);
    };

    const onSubmit = async () => {
        if (!isValid) {
            setShowErrors(true);
            return;
        }
        // Commit the draft for real before submitting, so the tokens left behind
        // in the form are the ones that were actually sent.
        if (effectiveForm !== form) {
            setForm(effectiveForm);
            setResourceDraft('');
        }
        setSubmitting(true);
        setSubmitError(undefined);
        try {
            const input = buildSubmitInput(effectiveForm, tenantOptions, adminMode);
            await createRequest(session.idToken, input);
            handleClose();
        } catch (err) {
            setSubmitError(err instanceof Error ? err.message : String(err));
            setSubmitting(false);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 'var(--en-radius-large)',
                    boxShadow: 'var(--en-shadow-menu)',
                    bgcolor: 'var(--en-bg)',
                },
            }}
        >
            <DialogTitle
                sx={{
                    px: 3,
                    pt: 2.5,
                    pb: 1,
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 1,
                }}
            >
                <Stack spacing={0.5} sx={{flex: 1, minWidth: 0}}>
                    <Typography
                        component="h2"
                        sx={{font: '700 18px/24px var(--en-font-sans)', color: 'var(--en-fg)'}}
                    >
                        {adminMode ? 'Grant access' : 'Request access'}
                    </Typography>
                    <Typography
                        sx={{font: '400 13px/18px var(--en-font-sans)', color: 'var(--en-fg-secondary)'}}
                    >
                        {adminMode
                            ? 'Pick a recipient, what they get, and for how long. Grants take effect immediately.'
                            : 'Pick what you need and for how long. An admin will review unless you’re trusted.'}
                    </Typography>
                </Stack>
                <IconButton
                    onClick={handleClose}
                    size="small"
                    aria-label="Close"
                    sx={{color: 'var(--en-icon)'}}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{px: 3, py: 1.5}} dividers={false}>
                <Stack spacing={2.5}>
                    {adminMode && (
                        <Field label="Recipient">
                            <RecipientField
                                value={form.recipient}
                                users={users ?? []}
                                invalid={showErrors && recipientProblem !== null}
                                onChange={u => setForm(f => ({...f, recipient: u}))}
                                onDraftChange={setRecipientDraft}
                            />
                            {showErrors && recipientProblem ? (
                                <Helper error>{recipientProblem}</Helper>
                            ) : null}
                        </Field>
                    )}

                    <Field label="Access type">
                        <KindGrid kind={form.kind} onChange={selectKind} />
                    </Field>

                    <Field label={resourceFieldLabel(form.kind)}>
                        <TenantsField
                            freeText={isSubjectAxis}
                            groupOf={tenantGroupOf}
                            value={form.tenants}
                            options={tenantValues}
                            labelOf={tenantLabelOf}
                            kind={form.kind}
                            invalid={showErrors && resourceProblem !== null}
                            // The subject axis is typed, not picked, so it does NOT
                            // depend on the tenant catalog. Disabling it when that
                            // catalog failed to load killed the one field the user
                            // came for, under a placeholder still inviting them to
                            // type in it — the literal "I can't enter an email" report.
                            disabled={!isSubjectAxis && tenantOptions.length === 0}
                            onChange={next => setForm(f => ({...f, tenants: next}))}
                            onDraftChange={setResourceDraft}
                        />
                        {showErrors && resourceProblem ? (
                            <Helper error>{resourceProblem}</Helper>
                        ) : (
                            <Helper>{tenantHelper(effectiveForm, tenantOptions.length === 0)}</Helper>
                        )}
                    </Field>

                    <Field label="Expires">
                        <ExpiresField
                            kind={activeKind}
                            expiry={form.expiry}
                            customDays={form.customDays}
                            onChange={next => setForm(f => ({...f, ...next}))}
                        />
                    </Field>

                    <Field label="Reason">
                        <TextField
                            multiline
                            minRows={4}
                            maxRows={8}
                            fullWidth
                            placeholder={REASON_PLACEHOLDER}
                            value={form.reason}
                            onChange={e => setForm(f => ({...f, reason: e.target.value}))}
                            error={showErrors && form.reason.trim().length < MIN_REASON_LENGTH}
                            sx={{
                                '& .MuiInputBase-root': {
                                    borderRadius: 'var(--en-radius-regular)',
                                    font: '400 14px/20px var(--en-font-sans)',
                                },
                            }}
                        />
                        <ReasonCounter length={form.reason.trim().length} />
                    </Field>

                    {submitError && (
                        <Box
                            sx={{
                                p: 1.25,
                                borderRadius: 'var(--en-radius-regular)',
                                bgcolor: 'rgba(252, 89, 67, 0.08)',
                                color: 'var(--en-error-dark)',
                                font: '500 13px/18px var(--en-font-sans)',
                            }}
                        >
                            {submitError}
                        </Box>
                    )}

                    <StatusBar issue={validation} showErrors={showErrors} />
                </Stack>
            </DialogContent>

            <DialogActions sx={{px: 3, py: 2, gap: 1}}>
                <button type="button" className="btn btn-text" onClick={handleClose} disabled={submitting}>
                    Cancel
                </button>
                <button
                    type="button"
                    className="btn btn-primary"
                    onClick={onSubmit}
                    disabled={submitting}
                    aria-disabled={!isValid}
                    style={!isValid ? {opacity: 0.55} : undefined}
                >
                    {submitting ? (adminMode ? 'Granting…' : 'Submitting…') : adminMode ? 'Grant access' : 'Submit'}
                </button>
            </DialogActions>
        </Dialog>
    );
}

// -- Field wrapper -----------------------------------------------------------

function Field({label, children}: {label: string; children: React.ReactNode}) {
    return (
        <Box>
            <Typography
                component="div"
                sx={{
                    font: '600 12px/16px var(--en-font-sans)',
                    color: 'var(--en-heading)',
                    mb: 0.75,
                }}
            >
                {label}
            </Typography>
            {children}
        </Box>
    );
}

function Helper({children, error}: {children: React.ReactNode; error?: boolean}) {
    return (
        <Typography
            sx={{
                mt: 0.5,
                font: error ? '500 12px/16px var(--en-font-sans)' : '400 12px/16px var(--en-font-sans)',
                color: error ? 'var(--en-error-dark)' : 'var(--en-fg-secondary)',
            }}
        >
            {children}
        </Typography>
    );
}

// -- Recipient field ---------------------------------------------------------

function RecipientField({
    value,
    users,
    invalid,
    onChange,
    onDraftChange,
}: {
    value: UserProfile | null;
    users: UserProfile[];
    invalid: boolean;
    onChange: (u: UserProfile | null) => void;
    /** The typed-but-not-yet-picked text. See `recipientDraft`. */
    onDraftChange?: (draft: string) => void;
}) {
    const [inputValue, setInputValue] = useState('');
    return (
        <Autocomplete
            options={users}
            value={value}
            // `clearOnBlur` defaults to true on a picker, so any text that had not
            // resolved to an option was wiped the moment focus left — including the
            // blur from clicking Grant access itself. The admin saw an empty box and
            // a form complaining about a field they had just filled in. Keep the
            // text; `recipientIssue` names it.
            clearOnBlur={false}
            // Together these make the obvious gesture work: type a colleague's
            // address, click Grant access, and the single match is committed on the
            // way out instead of being thrown away. `autoHighlight` is what gives
            // `autoSelect` something to commit.
            autoHighlight
            autoSelect
            // The UserBadge inside startAdornment carries its own X — hide the
            // default Autocomplete clear icon so there's a single, obvious
            // affordance in the picked state.
            sx={{'& .MuiAutocomplete-clearIndicator': {display: 'none'}}}
            // When a recipient is picked, suppress the input text so the
            // startAdornment badge is the only visible representation. The
            // X on the badge clears `value` and the input goes live again.
            inputValue={value ? '' : inputValue}
            onInputChange={(_e, v, reason) => {
                if (reason === 'reset' && value) {
                    return;
                }
                setInputValue(v);
                onDraftChange?.(v);
            }}
            onChange={(_e, v) => {
                onChange(v);
                if (!v) {
                    setInputValue('');
                }
                onDraftChange?.('');
            }}
            getOptionLabel={u => u.email}
            isOptionEqualToValue={(a, b) => a.email === b.email}
            filterOptions={(opts, state) => {
                const q = state.inputValue.trim().toLowerCase();
                if (!q) {
                    return opts.slice(0, 50);
                }
                return opts
                    .filter(
                        u =>
                            u.email.toLowerCase().includes(q) ||
                            nameFromEmail(u.email).toLowerCase().includes(q)
                    )
                    .slice(0, 50);
            }}
            renderOption={(props, u) => (
                <Box
                    component="li"
                    {...props}
                    key={u.email}
                    sx={{display: 'flex', gap: 1, alignItems: 'center'}}
                >
                    <UserBadge email={u.email} />
                    <Box
                        sx={{
                            font: '400 12px/16px var(--en-font-sans)',
                            color: 'var(--en-fg-secondary)',
                            ml: 'auto',
                        }}
                    >
                        {u.email}
                    </Box>
                </Box>
            )}
            renderInput={params => (
                <TextField
                    {...params}
                    placeholder={value ? '' : 'Search by email or name…'}
                    error={invalid}
                    InputProps={{
                        ...params.InputProps,
                        startAdornment: value ? (
                            <Box sx={{display: 'flex', alignItems: 'center', mr: 0.5}}>
                                <UserBadge email={value.email} onRemove={() => onChange(null)} />
                            </Box>
                        ) : (
                            params.InputProps.startAdornment
                        ),
                    }}
                    sx={{
                        '& .MuiInputBase-root': {
                            borderRadius: 'var(--en-radius-regular)',
                            font: '400 14px/20px var(--en-font-sans)',
                        },
                    }}
                />
            )}
        />
    );
}

// -- Access-type radio cards -------------------------------------------------

function KindGrid({kind, onChange}: {kind: KindId; onChange: (k: KindId) => void}) {
    return (
        <Box
            role="radiogroup"
            aria-label="Access type"
            sx={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr 1fr',
                gap: 1,
            }}
        >
            {KIND_LIST.map(meta => (
                <KindCard
                    key={meta.id}
                    meta={meta}
                    selected={kind === meta.id}
                    onSelect={() => onChange(meta.id)}
                />
            ))}
        </Box>
    );
}

function KindCard({
    meta,
    selected,
    onSelect,
}: {
    meta: KindMeta;
    selected: boolean;
    onSelect: () => void;
}) {
    const tintedBg = `color-mix(in srgb, ${meta.dotColor} 12%, transparent)`;
    const tintedBorder = `color-mix(in srgb, ${meta.dotColor} 55%, var(--en-divider))`;
    return (
        <Box
            role="radio"
            aria-checked={selected}
            tabIndex={0}
            onClick={onSelect}
            onKeyDown={e => {
                if (e.key === ' ' || e.key === 'Enter') {
                    e.preventDefault();
                    onSelect();
                }
            }}
            sx={{
                position: 'relative',
                p: 1.5,
                borderRadius: 'var(--en-radius-regular)',
                border: `1px solid ${selected ? tintedBorder : 'var(--en-divider)'}`,
                bgcolor: selected ? tintedBg : 'var(--en-bg)',
                cursor: 'pointer',
                transition: 'background-color var(--en-duration-short) var(--en-easing), border-color var(--en-duration-short) var(--en-easing)',
                outline: 'none',
                '&:focus-visible': {
                    boxShadow: '0 0 0 2px var(--en-primary)',
                },
            }}
        >
            <Box sx={{display: 'flex', alignItems: 'center', gap: 1, mb: 0.5}}>
                <Box
                    aria-hidden
                    sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        bgcolor: meta.dotColor,
                        flexShrink: 0,
                    }}
                />
                <Typography sx={{font: '600 14px/20px var(--en-font-sans)', color: 'var(--en-fg)'}}>
                    {meta.label}
                </Typography>
            </Box>
            <Typography sx={{font: '400 12px/16px var(--en-font-sans)', color: 'var(--en-fg-secondary)'}}>
                {meta.desc}
            </Typography>
        </Box>
    );
}

// -- Tenants field -----------------------------------------------------------

interface TenantsFieldProps {
    value: string[];
    options: string[];
    labelOf: (v: string) => string;
    /** Group heading per option, or undefined to render one flat list. */
    groupOf: (v: string) => string | undefined;
    /** Accept typed values, not just listed options (the subject axis). */
    freeText?: boolean;
    kind: KindId;
    invalid: boolean;
    disabled: boolean;
    onChange: (next: string[]) => void;
    /** The typed-but-not-yet-tokenized value. See `resourceDraft`. */
    onDraftChange?: (draft: string) => void;
}

/**
 * The resource axis is a different THING per access type, not a different label
 * for the same thing:
 *   - Data Access  → AWS accounts (tenants + the internal Ennabl AI accounts)
 *   - Decisions    → tenants
 *   - Ennabl AI    → SUBJECTS: who you may log in as (email / domain / `*`)
 */
function resourceFieldLabel(kind: KindId): string {
    if (kind === 'imp-ai') {
        return 'Impersonate as';
    }
    return resourceAxisLabel(kind) === 'Account' ? 'Tenants / AWS accounts' : 'Tenants';
}

/** Green tint marking an internal Ennabl AI account token. */
const AI_TOKEN_COLOR = 'hsl(165 62% 45%)';

function TenantsField({
    value,
    options,
    labelOf,
    groupOf,
    freeText,
    kind,
    invalid,
    disabled,
    onChange,
    onDraftChange,
}: TenantsFieldProps) {
    const dotColor = KIND_BY_ID[kind]?.dotColor ?? 'var(--en-primary)';
    const chipBg = `color-mix(in srgb, ${dotColor} 14%, transparent)`;
    const chipFg = `color-mix(in srgb, ${dotColor} 75%, var(--en-fg))`;
    // Groups are only present on Data Access; elsewhere every option returns
    // undefined and MUI renders a flat list.
    const grouped = options.some(o => groupOf(o) !== undefined);
    return (
        <Autocomplete
            multiple
            options={options}
            value={value}
            disabled={disabled}
            disableCloseOnSelect
            freeSolo={freeText}
            // freeSolo alone commits on Enter and nothing else, so tabbing or
            // clicking away threw the text out. `autoSelect` turns it into a token
            // on blur — which is also what makes clicking Grant access work.
            autoSelect={freeText}
            // On a picker `clearOnBlur` defaults to true, which wipes unmatched
            // text the moment focus leaves — including the blur from clicking
            // Grant access, so the operator sees an empty box and a complaint
            // about a field they just filled in. Keep the text; the form names it.
            // Selecting a real option still clears the box (useAutocomplete only
            // honours this when nothing was selected).
            clearOnBlur={false}
            groupBy={grouped ? o => groupOf(o) ?? TENANT_GROUP_LABEL : undefined}
            getOptionLabel={labelOf}
            // Omitted under freeSolo: a typed value is not one of `options`, and MUI
            // warns when both are supplied.
            {...(freeText ? {} : {isOptionEqualToValue: (a: string, b: string) => a === b})}
            onInputChange={(_e, next, reason) => {
                // With `clearOnBlur` off, `reset` fires only once an option really
                // was selected; reporting that as a draft would double-count the
                // token just created.
                onDraftChange?.(reason === 'reset' ? '' : next);
            }}
            onChange={(event, raw, reason) => {
                // `autoSelect` commits the HIGHLIGHTED option when focus leaves, and
                // in a multiple Autocomplete committing an option that is already
                // selected REMOVES it (MUI's selectNewValue toggles). On the subject
                // axis the option list is just `*`, so once the wildcard was picked
                // it stayed highlighted and the next blur — tab, click on Reason,
                // click on Submit — silently deleted the chip that had just been
                // created. A blur may add a token; it may never take one away.
                // Deleting via the chip's X is the same `reason` but a click event.
                if (reason === 'removeOption' && event?.type === 'blur') {
                    return;
                }
                // Normalize typed subjects (trim + lowercase, `@som.com` -> domain)
                // so casing or a stray space cannot yield two tokens that mean the
                // same grant. Text that is not a valid subject is KEPT as a token
                // rather than dropped — Submit blocks with a reason, which beats
                // silently swallowing what someone just typed.
                const next = freeText
                    ? Array.from(
                          new Set(
                              (raw as string[]).map(v =>
                                  v === WILDCARD ? v : normalizeImpAiSubject(v) ?? v.trim()
                              )
                          )
                      )
                    : (raw as string[]);
                // Wildcard is exclusive: selecting it clears specific tenants;
                // selecting a specific tenant clears the wildcard.
                if (next.includes(WILDCARD) && !value.includes(WILDCARD)) {
                    onChange([WILDCARD]);
                    return;
                }
                if (value.includes(WILDCARD) && next.length > 1) {
                    onChange(next.filter(v => v !== WILDCARD));
                    return;
                }
                onChange(next);
            }}
            renderTags={(values, getTagProps) =>
                values.map((v, idx) => {
                    const tagProps = getTagProps({index: idx});
                    const isAi = isAiAccount(v);
                    return (
                        <Chip
                            {...tagProps}
                            key={v}
                            size="small"
                            label={v === WILDCARD ? 'All tenants (*)' : isAi ? aiAccountLabel(v) : v}
                            sx={{
                                bgcolor: isAi
                                    ? `color-mix(in srgb, ${AI_TOKEN_COLOR} 14%, transparent)`
                                    : chipBg,
                                color: isAi
                                    ? `color-mix(in srgb, ${AI_TOKEN_COLOR} 78%, var(--en-fg))`
                                    : chipFg,
                                font: '600 12px/16px var(--en-font-sans)',
                                borderRadius: 'var(--en-radius)',
                                '& .MuiChip-deleteIcon': {
                                    color: 'inherit',
                                    opacity: 0.6,
                                    '&:hover': {opacity: 1},
                                },
                            }}
                        />
                    );
                })
            }
            renderInput={params => (
                <TextField
                    {...params}
                    placeholder={
                        value.length === 0
                            ? freeText
                                ? 'Type an email or domain, then Enter (or * for everyone)…'
                                : 'Type to search tenants, or pick * for all…'
                            : value.includes(WILDCARD)
                              ? ''
                              : 'Add another…'
                    }
                    error={invalid}
                    sx={{
                        '& .MuiInputBase-root': {
                            borderRadius: 'var(--en-radius-regular)',
                            font: '400 14px/20px var(--en-font-sans)',
                        },
                    }}
                />
            )}
        />
    );
}

// -- Expires field -----------------------------------------------------------

interface ExpiresFieldProps {
    kind: KindMeta;
    expiry: ExpiryChoice;
    customDays: number;
    onChange: (next: Partial<Pick<FormState, 'expiry' | 'customDays'>>) => void;
}

const EXPIRY_CHOICES: Array<{value: ExpiryChoice; label: React.ReactNode}> = [
    {value: '1d', label: '1 day'},
    {value: '7d', label: '7 days'},
    {value: 'custom', label: 'Custom'},
    {value: 'permanent', label: '∞'},
];

function ExpiresField({kind, expiry, customDays, onChange}: ExpiresFieldProps) {
    return (
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <ToggleButtonGroup
                value={expiry}
                exclusive
                size="small"
                onChange={(_e, next: ExpiryChoice | null) => {
                    if (!next) {
                        return;
                    }
                    if (kind.lockedExpiry && next !== kind.lockedExpiry) {
                        return;
                    }
                    onChange({expiry: next});
                }}
                sx={{
                    '& .MuiToggleButton-root': {
                        height: 30,
                        textTransform: 'none',
                        font: '500 13px/18px var(--en-font-sans)',
                        color: 'var(--en-fg-secondary)',
                        borderColor: 'var(--en-divider)',
                        px: 1.5,
                        py: 0,
                        '&.Mui-selected': {
                            bgcolor: 'var(--en-bg-active)',
                            color: 'var(--en-text-tertiary)',
                            borderColor: 'var(--en-bg-primary)',
                            '&:hover': {bgcolor: 'var(--en-bg-active)'},
                        },
                        '&.Mui-disabled': {
                            color: 'var(--en-fg-disabled)',
                        },
                    },
                }}
            >
                {EXPIRY_CHOICES.map(choice => {
                    const locked = Boolean(kind.lockedExpiry && choice.value !== kind.lockedExpiry);
                    const btn = (
                        <ToggleButton key={choice.value} value={choice.value} disabled={locked}>
                            {choice.label}
                        </ToggleButton>
                    );
                    return locked ? (
                        <Tooltip key={choice.value} title={kind.lockedNote ?? ''} placement="top" arrow>
                            <span>{btn}</span>
                        </Tooltip>
                    ) : (
                        btn
                    );
                })}
            </ToggleButtonGroup>
            {expiry === 'custom' && (
                <TextField
                    type="number"
                    size="small"
                    value={customDays}
                    inputProps={{min: 1, max: 365, style: {width: 48, padding: 0}}}
                    onChange={e => {
                        const n = Number(e.target.value);
                        if (Number.isFinite(n) && n >= 1) {
                            onChange({customDays: Math.min(365, Math.floor(n))});
                        }
                    }}
                    sx={{
                        '& .MuiInputBase-root': {
                            height: 30,
                            borderRadius: 'var(--en-radius-regular)',
                            font: '500 13px/18px var(--en-font-sans)',
                            color: 'var(--en-fg-secondary)',
                            paddingLeft: '12px',
                            paddingRight: '8px',
                        },
                        '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'var(--en-divider)',
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'var(--en-divider)',
                        },
                        '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'var(--en-bg-primary)',
                        },
                    }}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end" sx={{ml: 0.5}}>
                                <Typography
                                    sx={{
                                        font: '500 13px/18px var(--en-font-sans)',
                                        color: 'var(--en-fg-secondary)',
                                    }}
                                >
                                    days
                                </Typography>
                            </InputAdornment>
                        ),
                    }}
                />
            )}
        </Stack>
    );
}

// -- Reason counter ----------------------------------------------------------

function ReasonCounter({length}: {length: number}) {
    const ok = length >= MIN_REASON_LENGTH;
    return (
        <Box sx={{display: 'flex', justifyContent: 'flex-end', mt: 0.5}}>
            <Typography
                sx={{
                    font: '500 12px/16px var(--en-font-sans)',
                    color: ok ? 'var(--en-success-dark)' : 'var(--en-fg-secondary)',
                }}
            >
                {length}/{MIN_REASON_LENGTH} min
            </Typography>
        </Box>
    );
}

// -- Status bar --------------------------------------------------------------

function StatusBar({issue, showErrors}: {issue: string | null; showErrors: boolean}) {
    const ready = issue === null;
    const tone = ready ? 'success' : showErrors ? 'error' : 'neutral';
    const message = ready ? 'Ready — grant will fire immediately on submit.' : issue;
    const palette = {
        success: {bg: 'rgba(61, 213, 152, 0.12)', fg: 'var(--en-success-dark)', dot: 'var(--en-success)'},
        error: {bg: 'rgba(252, 89, 67, 0.12)', fg: 'var(--en-error-dark)', dot: 'var(--en-error)'},
        neutral: {bg: 'var(--en-bg-active)', fg: 'var(--en-text-tertiary)', dot: 'var(--en-bg-primary)'},
    }[tone];

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                bgcolor: palette.bg,
                color: palette.fg,
                px: 1.5,
                py: 1,
                borderRadius: 'var(--en-radius-regular)',
                font: '500 13px/18px var(--en-font-sans)',
            }}
        >
            <Box
                aria-hidden
                sx={{width: 6, height: 6, borderRadius: '50%', bgcolor: palette.dot, flexShrink: 0}}
            />
            <span>{message}</span>
        </Box>
    );
}

function CloseIcon() {
    return (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
            <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
    );
}

// -- Helpers -----------------------------------------------------------------

function buildTenantOptions(grants: GrantView[], catalog: TenantCatalogEntry[]): TenantOption[] {
    const byCanonical = new Map<string, TenantOption>();
    // Seed from the full catalog so every tenant is selectable, even with zero
    // grants. Only the shard uid (`id`) goes into `ids` — that's what the grant
    // flows expect as a tenantId; the account id is display/match only.
    for (const t of catalog) {
        if (!t.name || !t.id) {
            continue;
        }
        let entry = byCanonical.get(t.name);
        if (!entry) {
            entry = {value: t.name, label: t.name, ids: []};
            byCanonical.set(t.name, entry);
        }
        if (!entry.ids.includes(t.id)) {
            entry.ids.push(t.id);
        }
        entry.label = `${t.name} · ${entry.ids.join(', ')}`;
    }
    // Enrich/extend with whatever the grants snapshot carries — covers ids the
    // catalog might lack and keeps the picker populated if the catalog fetch
    // failed (empty `catalog`).
    for (const g of grants) {
        if (g.resource === '*' || g.resource === '?') {
            continue;
        }
        if (AWS_ACCOUNT_ID.test(g.resource)) {
            continue;
        }
        const canon = g.resourceName ?? g.resource;
        let entry = byCanonical.get(canon);
        if (!entry) {
            entry = {value: canon, label: canon, ids: []};
            byCanonical.set(canon, entry);
        }
        if (!entry.ids.includes(g.resource)) {
            entry.ids.push(g.resource);
        }
        entry.label = g.resourceName ? `${g.resourceName} · ${entry.ids.join(', ')}` : entry.ids.join(', ');
    }
    const out = Array.from(byCanonical.values());
    out.sort((a, b) => a.label.localeCompare(b.label));
    return out;
}

/**
 * The Expires segmented control as a number of days, or undefined for permanent.
 *
 * Only the Ennabl AI path sends this today: DataAccess TTL still lives in the
 * bastion Lambda, and Decisions impersonation is ∞ by definition.
 */
function expiryDaysOf(form: FormState): number | undefined {
    switch (form.expiry) {
        case '1d':
            return 1;
        case '7d':
            return 7;
        case 'custom':
            return form.customDays > 0 ? form.customDays : undefined;
        case 'permanent':
            return undefined;
    }
}

function tenantHelper(form: FormState, optionsEmpty: boolean): string {
    if (form.kind === 'imp-ai') {
        // The subject axis is typed, not picked, so the helper has to teach the
        // three levels instead of describing a selection count.
        if (form.tenants.includes(WILDCARD)) {
            return 'Everyone — this grants impersonation of any Ennabl AI user.';
        }
        if (form.tenants.length === 0) {
            return 'Type an email (a@b.com), a domain (b.com or *@b.com) for everyone there, or * for everyone.';
        }
        const domains = form.tenants.filter(t => classifyImpAiSubject(t)?.kind === 'domain');
        if (domains.length > 0) {
            return `${form.tenants.length} subject${form.tenants.length === 1 ? '' : 's'} — ${domains.join(', ')} covers every user at that domain.`;
        }
        return `${form.tenants.length} subject${form.tenants.length === 1 ? '' : 's'}.`;
    }
    if (optionsEmpty) {
        // The catalog fetch is fire-and-forget and never retries, so "wait" was
        // advice that could not come true. Reloading is the thing that works.
        return 'No tenants loaded — reload the page with Twingate connected.';
    }
    if (form.tenants.includes(WILDCARD)) {
        return 'Wildcard selected — applies to every tenant.';
    }
    return form.tenants.length === 0
        ? 'Pick at least one tenant, or * for all.'
        : `${form.tenants.length} selected.`;
}

/**
 * `form` with a typed-but-uncommitted subject folded into `tenants`.
 *
 * Normalized the same way the field's own `onChange` normalizes a committed one,
 * so validating the draft answers the same question as validating the token, and
 * a subject already in the list does not appear twice. Returns `form` unchanged
 * when there is nothing to fold in — identity is what lets the caller tell
 * "nothing to commit" from "commit this" without comparing arrays.
 */
function withSubjectDraft(form: FormState, draft: string): FormState {
    const raw = draft.trim();
    if (!raw) {
        return form;
    }
    const value = raw === WILDCARD ? WILDCARD : normalizeImpAiSubject(raw) ?? raw;
    if (value === WILDCARD) {
        return {...form, tenants: [WILDCARD]};
    }
    if (form.tenants.includes(value)) {
        return form;
    }
    return {...form, tenants: [...form.tenants.filter(t => t !== WILDCARD), value]};
}

/**
 * The resource-axis half of validation, split out so the reason can be rendered
 * under the field it is about. A red border whose explanation lives two fields
 * below the fold is the same as no explanation at all.
 */
function resourceIssue(form: FormState, unmatchedDraft: string): string | null {
    if (unmatchedDraft) {
        // Says the thing the empty-field message cannot: this axis is a catalog,
        // and the text is not in it. "Add at least one tenant" next to a box the
        // operator has just typed into reads as a bug in the form.
        const axis = resourceAxisLabel(form.kind) === 'Account' ? 'tenant or AWS account' : 'tenant';
        return `"${unmatchedDraft}" is not a ${axis} — pick one from the list.`;
    }
    if (form.tenants.length === 0) {
        return form.kind === 'imp-ai' ? 'Add at least one subject.' : 'Add at least one tenant.';
    }
    if (form.kind === 'imp-ai') {
        // Names the offending token rather than a generic "invalid input": with a
        // free-text field the whole question is WHICH one is wrong.
        const bad = form.tenants.find(t => t !== WILDCARD && impAiSubjectError(t) !== null);
        if (bad) {
            return impAiSubjectError(bad) as string;
        }
    }
    return null;
}

/** Does any roster entry match what was typed? The picker's own predicate. */
function matchesRecipient(users: UserProfile[], raw: string): boolean {
    const q = raw.trim().toLowerCase();
    return users.some(
        u => u.email.toLowerCase().includes(q) || nameFromEmail(u.email).toLowerCase().includes(q)
    );
}

/**
 * The recipient half of validation, split out so it can be rendered under the
 * Recipient field. "Pick a recipient to continue" under a box with an address
 * visibly typed into it reads as a bug in the form; the roster being limited to
 * people who have signed in at least once is the thing that actually needs
 * saying, along with what to do about it.
 */
function recipientIssue(
    form: FormState,
    adminMode: boolean,
    unmatchedRecipient: string
): string | null {
    if (!adminMode || form.recipient) {
        return null;
    }
    if (unmatchedRecipient) {
        return `"${unmatchedRecipient}" isn't in the list — they need to sign in to AccessOps once before you can grant to them.`;
    }
    return 'Pick a recipient to continue.';
}

function firstIssue(
    form: FormState,
    adminMode: boolean,
    unmatchedDraft: string,
    unmatchedRecipient: string
): string | null {
    const recipient = recipientIssue(form, adminMode, unmatchedRecipient);
    if (recipient) {
        return recipient;
    }
    const resource = resourceIssue(form, unmatchedDraft);
    if (resource) {
        return resource;
    }
    const reasonLen = form.reason.trim().length;
    if (reasonLen < MIN_REASON_LENGTH) {
        const missing = MIN_REASON_LENGTH - reasonLen;
        return `Reason needs ${missing} more character${missing === 1 ? '' : 's'}.`;
    }
    return null;
}

/**
 * Translate the new dialog form state into the existing POST /requests payload.
 * `kind` maps to one of `dataAccess` / `impersonation` — backend untouched.
 * Per-block `expiry` is captured in form state but not transmitted; the bastion
 * temp policy is still 24h regardless.
 */
function buildSubmitInput(
    form: FormState,
    tenantOptions: TenantOption[],
    adminMode: boolean
): CreateRequestInput {
    const lookup = new Map(tenantOptions.map(o => [o.value, o.ids]));
    const expand = (selected: string[]): string[] => {
        if (selected.includes(WILDCARD)) {
            return [WILDCARD];
        }
        const ids: string[] = [];
        for (const v of selected) {
            for (const id of lookup.get(v) ?? [v]) {
                if (!ids.includes(id)) {
                    ids.push(id);
                }
            }
        }
        return ids;
    };

    const input: CreateRequestInput = {reason: form.reason.trim()};
    const tenantIds = expand(form.tenants);
    if (form.kind === 'data-access') {
        // Permanent Data Access only exists for wildcard on the backend today;
        // coerce non-wildcard permanent picks to '*' to match the bastion path.
        if (form.expiry === 'permanent' && !tenantIds.includes(WILDCARD)) {
            input.dataAccess = {tenantIds: [WILDCARD]};
        } else {
            input.dataAccess = {tenantIds};
        }
    } else if (form.kind === 'imp-decisions') {
        input.impersonation = {tenantIds};
    } else if (form.kind === 'imp-ai') {
        // The subject axis needs no id expansion — a subject IS the value typed,
        // there is no name-to-uid lookup behind it.
        input.impAi = {tenantIds: form.tenants, expiryDays: expiryDaysOf(form)};
    }
    if (adminMode && form.recipient) {
        input.onBehalfOf = form.recipient.email;
    }
    return input;
}
